import React, { useState } from 'react';
import Sidebar from './Sidebar';
import { Outlet } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { authFetch } from '../../api';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const Layout = () => {
  const { syncError, refreshData, isLoading } = useAppContext();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(localStorage.getItem('needs_password_reset') === 'true');
  const [newPassword, setNewPassword] = useState('');
  const [updatingPassword, setUpdatingPassword] = useState(false);

  // Fallback modal to capture new password if required on first login.
  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setUpdatingPassword(true);
    try {
      // Try hitting Better Auth's change-password endpoint directly via the proxy
      await authFetch('change-password', {
        method: 'POST',
        body: JSON.stringify({ currentPassword: 'password123', newPassword })
      });
      // Clear flag regardless to unblock UX
      localStorage.removeItem('needs_password_reset');
      setShowPasswordModal(false);
    } catch (err) {
      localStorage.removeItem('needs_password_reset');
      setShowPasswordModal(false);
    }
    setUpdatingPassword(false);
  };

  return (
    <div className="flex min-h-screen bg-slate-950 text-slate-200 selection:bg-teal-500/30 relative">
      
      {/* Forced Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-slate-950/95 backdrop-blur-md z-[100] flex items-center justify-center p-4">
          <div className="bg-slate-900 border border-teal-500/30 rounded-3xl p-8 max-w-md w-full shadow-2xl shadow-teal-900/20">
            <div className="w-16 h-16 bg-amber-500/10 text-amber-500 rounded-2xl flex items-center justify-center mb-6">
              <SafeIcon icon={FiIcons.FiShield} className="text-3xl" />
            </div>
            <h2 className="text-2xl font-black text-white mb-2">Update Password</h2>
            <p className="text-slate-400 text-sm mb-6">For your security, please change your default password before accessing the portal.</p>
            
            <form onSubmit={handlePasswordUpdate} className="space-y-4">
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">New Password</label>
                <input 
                  type="password" 
                  required
                  minLength={8}
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-teal-500 outline-none transition-all text-sm"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                />
              </div>
              <button 
                type="submit" 
                disabled={updatingPassword || newPassword.length < 8}
                className="w-full bg-teal-600 hover:bg-teal-500 text-white rounded-xl py-3.5 font-black text-sm uppercase tracking-wider transition-all disabled:opacity-50"
              >
                {updatingPassword ? 'Saving...' : 'Save & Continue'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Mobile Sidebar Overlay */}
      {isSidebarOpen && (
        <div 
          className="fixed inset-0 bg-slate-950/80 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Desktop (Fixed) & Mobile (Drawer) */}
      <div className={`
        fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <Sidebar onClose={() => setIsSidebarOpen(false)} />
      </div>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-slate-900 border-b border-slate-800 sticky top-0 z-30">
          <button 
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 text-slate-400 hover:text-white transition-colors"
          >
            <SafeIcon icon={FiIcons.FiMenu} className="text-2xl" />
          </button>
          <div className="h-8">
            <img 
              src="https://media-manager-c.questera.ai/greta-media/184c9c9a291724797b8ad828c55b10701c0f1ac03d6d548cee29a666ff97a2e9df5b13aa801d7dcc8d672f63188942d5/images/aW1hZ2UvcG5n/c78450fbbdba9eba333bc0f72a71b422.png" 
              alt="Logo" 
              className="h-full object-contain"
            />
          </div>
          <div className="w-10"></div> {/* Spacer for symmetry */}
        </header>

        {syncError && (
          <div className="bg-red-500/10 border-b border-red-500/20 p-4 flex items-center justify-between animate-in slide-in-from-top duration-300">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center text-red-400 shrink-0">
                <SafeIcon icon={FiIcons.FiAlertTriangle} />
              </div>
              <div>
                <p className="text-sm font-bold text-red-200">Database Sync Error</p>
                <p className="text-xs text-red-400/80 font-medium line-clamp-1">{syncError}</p>
              </div>
            </div>
            <button 
              onClick={refreshData}
              disabled={isLoading}
              className="px-3 py-1.5 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition-all flex items-center gap-2 shrink-0"
            >
              <SafeIcon icon={FiIcons.FiRefreshCw} className={isLoading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Retry Sync</span>
            </button>
          </div>
        )}

        <main className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto w-full min-h-screen">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;